import initSqlJs, { type Database, type SqlJsStatic } from 'sql.js';
import wasmUrl from 'sql.js/dist/sql-wasm.wasm?url';
import { CONTENT_DIMENSION_KEY, type ClusterDraft, type ClusterInput, type ClusterLeafKind, type ClusterVectorCache, type CreateEntry, type DataPort, type EmbeddingOwnerKind, type EnrichmentJob, type EnrichmentPayload, type EnrichmentTaskResult, type ExtractionRecord, type InterruptedDocument, type SourceRecord, type SummaryRecord, type TeacherAnnotationInput, type WorkNodeRecord } from './contract';
import { readDatabase, writeDatabase } from './indexedDb';
import type { EntryState, TimelineItem } from '../types';

const RETIRED_DIMENSION_KEYS = ['desire', 'goal', 'duty', 'time_suitability', 'people', 'projects', 'roles', 'places', 'resources', 'topics'];
let sqlitePromise: Promise<SqlJsStatic> | null = null;

function sqlite() {
  sqlitePromise ||= initSqlJs({ locateFile: () => wasmUrl });
  return sqlitePromise;
}

type SqlValue = string | number | null | Uint8Array;
type SqlRecord = Record<string, SqlValue>;

function records(db: Database, query: string, params: Array<string | number | null> = []): SqlRecord[] {
  const result = db.exec(query, params)[0];
  return result?.values.map((values) => Object.fromEntries(result.columns.map((column, index) => [column, values[index] as SqlValue]))) ?? [];
}

function toBase64(bytes: Uint8Array | null | undefined) {
  if (!bytes) return null;
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 0x8000) binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  return btoa(binary);
}

function fromBase64(value: unknown) {
  if (typeof value !== 'string' || !value) return null;
  const binary = atob(value.replace(/\s/g, ''));
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

const SCHEMA = `
PRAGMA foreign_keys = ON;
CREATE TABLE IF NOT EXISTS users (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  external_key TEXT NOT NULL UNIQUE,
  name TEXT NOT NULL,
  created_at TEXT NOT NULL
);
CREATE TABLE IF NOT EXISTS sources (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  kind TEXT NOT NULL CHECK(kind IN ('document','audio')),
  name TEXT NOT NULL,
  mime_type TEXT,
  content BLOB,
  remote_id INTEGER,
  created_at TEXT NOT NULL,
  user_id INTEGER REFERENCES users(id) ON DELETE SET NULL
);
CREATE TABLE IF NOT EXISTS input_entries (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  source_id INTEGER REFERENCES sources(id) ON DELETE SET NULL,
  text TEXT NOT NULL,
  source_kind TEXT NOT NULL CHECK(source_kind IN ('text','voice','remote')),
  state TEXT NOT NULL CHECK(state IN ('active','completed','pending_reflection')),
  scheduled_at TEXT,
  created_at TEXT NOT NULL,
  updated_at TEXT NOT NULL,
  remote_id INTEGER,
  user_id INTEGER REFERENCES users(id) ON DELETE SET NULL
);
CREATE TABLE IF NOT EXISTS work_nodes (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  entry_id INTEGER REFERENCES input_entries(id) ON DELETE CASCADE,
  source_id INTEGER REFERENCES sources(id) ON DELETE CASCADE,
  user_id INTEGER REFERENCES users(id) ON DELETE SET NULL,
  kind TEXT NOT NULL CHECK(kind IN ('note','event','question')),
  text TEXT NOT NULL,
  ordinal INTEGER NOT NULL DEFAULT 0,
  weight INTEGER NOT NULL DEFAULT 1 CHECK(weight >= 1),
  state TEXT NOT NULL,
  scheduled_at TEXT,
  created_at TEXT NOT NULL
  ,remote_id INTEGER
  ,CHECK ((entry_id IS NOT NULL) != (source_id IS NOT NULL))
);
CREATE TABLE IF NOT EXISTS summaries (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  source_id INTEGER NOT NULL REFERENCES sources(id) ON DELETE CASCADE,
  user_id INTEGER REFERENCES users(id) ON DELETE SET NULL,
  text TEXT NOT NULL,
  level INTEGER NOT NULL DEFAULT 1 CHECK(level >= 1),
  ordinal INTEGER NOT NULL DEFAULT 0,
  start_ordinal INTEGER NOT NULL DEFAULT 0,
  end_ordinal INTEGER NOT NULL DEFAULT 0,
  created_at TEXT NOT NULL,
  remote_id INTEGER,
  UNIQUE(source_id,level,ordinal)
);
CREATE TABLE IF NOT EXISTS extractions (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  node_id INTEGER NOT NULL REFERENCES work_nodes(id) ON DELETE CASCADE,
  dimension_key TEXT NOT NULL,
  start_char INTEGER NOT NULL CHECK(start_char >= 0),
  end_char INTEGER NOT NULL CHECK(end_char >= start_char),
  created_at TEXT NOT NULL,
  remote_id INTEGER
);
CREATE TABLE IF NOT EXISTS explanation_edges (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  from_extraction_id INTEGER NOT NULL REFERENCES extractions(id) ON DELETE CASCADE,
  to_extraction_id INTEGER NOT NULL REFERENCES extractions(id) ON DELETE CASCADE,
  relation TEXT NOT NULL,
  remote_id INTEGER,
  UNIQUE(from_extraction_id, to_extraction_id, relation)
);
CREATE TABLE IF NOT EXISTS teacher_annotations (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  entry_id INTEGER NOT NULL UNIQUE REFERENCES input_entries(id) ON DELETE CASCADE,
  user_id INTEGER REFERENCES users(id) ON DELETE SET NULL,
  source_text TEXT NOT NULL,
  model TEXT NOT NULL,
  prompt_key TEXT NOT NULL,
  prompt_version TEXT NOT NULL,
  schema_version TEXT NOT NULL,
  tokenizer_version TEXT NOT NULL,
  input_hash TEXT NOT NULL,
  payload TEXT NOT NULL,
  created_at TEXT NOT NULL,
  remote_id INTEGER UNIQUE
);
CREATE TABLE IF NOT EXISTS clusters (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  dimension_key TEXT NOT NULL,
  level INTEGER NOT NULL CHECK(level >= 1),
  text TEXT NOT NULL,
  embedding BLOB,
  created_at TEXT NOT NULL,
  user_id INTEGER REFERENCES users(id) ON DELETE SET NULL,
  remote_id INTEGER
);
CREATE TABLE IF NOT EXISTS cluster_members (
  cluster_id INTEGER NOT NULL REFERENCES clusters(id) ON DELETE CASCADE,
  member_kind TEXT NOT NULL CHECK(member_kind IN ('source','entry','node','summary','extraction','cluster')),
  member_id INTEGER NOT NULL,
  PRIMARY KEY(cluster_id, member_kind, member_id)
);
CREATE TABLE IF NOT EXISTS embeddings (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  owner_kind TEXT NOT NULL,
  owner_id INTEGER NOT NULL,
  dimension_key TEXT,
  model TEXT NOT NULL,
  dimensions INTEGER NOT NULL,
  vector TEXT NOT NULL,
  created_at TEXT NOT NULL,
  UNIQUE(owner_kind, owner_id, dimension_key, model)
);
CREATE TABLE IF NOT EXISTS outbox (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  operation TEXT NOT NULL,
  entity_id INTEGER NOT NULL,
  payload TEXT NOT NULL,
  created_at TEXT NOT NULL
);
CREATE TABLE IF NOT EXISTS sync_state (key TEXT PRIMARY KEY, value TEXT NOT NULL);
CREATE TABLE IF NOT EXISTS enrichment_jobs (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  job_key TEXT NOT NULL UNIQUE,
  payload TEXT NOT NULL,
  status TEXT NOT NULL CHECK(status IN ('pending','running','completed','failed')),
  attempts INTEGER NOT NULL DEFAULT 0,
  next_attempt_at TEXT NOT NULL,
  last_error TEXT,
  created_at TEXT NOT NULL,
  updated_at TEXT NOT NULL
);
CREATE TABLE IF NOT EXISTS enrichment_task_results (
  job_id INTEGER NOT NULL,
  task_key TEXT NOT NULL,
  result TEXT NOT NULL,
  completed_at TEXT NOT NULL,
  PRIMARY KEY(job_id,task_key)
);
CREATE INDEX IF NOT EXISTS extractions_node_idx ON extractions(node_id);
CREATE INDEX IF NOT EXISTS extractions_dimension_idx ON extractions(dimension_key);
CREATE INDEX IF NOT EXISTS explanation_edges_from_idx ON explanation_edges(from_extraction_id);
CREATE INDEX IF NOT EXISTS explanation_edges_to_idx ON explanation_edges(to_extraction_id);
CREATE INDEX IF NOT EXISTS clusters_dimension_level_idx ON clusters(dimension_key,level);
CREATE INDEX IF NOT EXISTS enrichment_jobs_due_idx ON enrichment_jobs(status,next_attempt_at);
`;

export class BrowserSqliteData implements DataPort {
  readonly location = 'local' as const;
  private readonly file: string;
  private db: Database | null = null;
  private persistQueue: Promise<void> = Promise.resolve();
  private writeBatchDepth = 0;
  private writeBatchDirty = false;

  constructor(accountKey = 'default') {
    this.file = `graphite-teacher-v1:${encodeURIComponent(accountKey)}.sqlite`;
  }

  private async database() {
    if (this.db) return this.db;
    const SQL = await sqlite();
    const bytes = await readDatabase(this.file);
    this.db = bytes ? new SQL.Database(bytes) : new SQL.Database();
    this.db.run(SCHEMA);
    this.migrate();
    const now = new Date().toISOString();
    this.db.run(`INSERT OR IGNORE INTO users(id,external_key,name,created_at) VALUES (1,'default','Основной пользователь',?)`, [now]);
    await this.persist();
    return this.db;
  }

  private migrate() {
    if (!this.db) return;
    this.db.run('DROP TABLE IF EXISTS change_log');
    const add = (table: string, column: string, declaration: string) => {
      const result = this.db!.exec(`PRAGMA table_info(${table})`)[0];
      const names = result?.values.map((row) => String(row[1])) ?? [];
      if (!names.includes(column)) this.db!.run(`ALTER TABLE ${table} ADD COLUMN ${declaration}`);
    };
    add('sources', 'user_id', 'user_id INTEGER REFERENCES users(id) ON DELETE SET NULL');
    add('sources', 'remote_id', 'remote_id INTEGER');
    add('input_entries', 'user_id', 'user_id INTEGER REFERENCES users(id) ON DELETE SET NULL');
    add('clusters', 'user_id', 'user_id INTEGER REFERENCES users(id) ON DELETE SET NULL');
    add('clusters', 'remote_id', 'remote_id INTEGER');
    add('extractions', 'remote_id', 'remote_id INTEGER');
    add('explanation_edges', 'remote_id', 'remote_id INTEGER');
    const nodeInfo = this.db.exec('PRAGMA table_info(work_nodes)')[0];
    const nodeColumns = new Map(nodeInfo?.values.map((row) => [String(row[1]), Number(row[3])]) ?? []);
    if (!nodeColumns.has('source_id') || !nodeColumns.has('user_id') || nodeColumns.get('entry_id') === 1) {
      this.db.run('PRAGMA foreign_keys=OFF');
      this.db.run(`CREATE TABLE work_nodes_next (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        entry_id INTEGER REFERENCES input_entries(id) ON DELETE CASCADE,
        source_id INTEGER REFERENCES sources(id) ON DELETE CASCADE,
        user_id INTEGER REFERENCES users(id) ON DELETE SET NULL,
        kind TEXT NOT NULL CHECK(kind IN ('note','event','question')),
        text TEXT NOT NULL, ordinal INTEGER NOT NULL DEFAULT 0, heading_path TEXT,
        state TEXT NOT NULL, scheduled_at TEXT, created_at TEXT NOT NULL, remote_id INTEGER,
        CHECK ((entry_id IS NOT NULL) != (source_id IS NOT NULL))
      )`);
      this.db.run(`INSERT INTO work_nodes_next(id,entry_id,source_id,user_id,kind,text,ordinal,heading_path,state,scheduled_at,created_at,remote_id)
        SELECT n.id,n.entry_id,NULL,i.user_id,n.kind,n.text,0,NULL,n.state,n.scheduled_at,n.created_at,NULL
        FROM work_nodes n LEFT JOIN input_entries i ON i.id=n.entry_id`);
      this.db.run('DROP TABLE work_nodes');
      this.db.run('ALTER TABLE work_nodes_next RENAME TO work_nodes');
      this.db.run('PRAGMA foreign_keys=ON');
    }
    add('work_nodes', 'remote_id', 'remote_id INTEGER');
    add('work_nodes', 'ordinal', 'ordinal INTEGER NOT NULL DEFAULT 0');
    const currentNodeColumns = new Set((this.db.exec('PRAGMA table_info(work_nodes)')[0]?.values || []).map((row) => String(row[1])));
    if (!currentNodeColumns.has('weight') || ['heading_path', 'heading_level', 'is_heading'].some((column) => currentNodeColumns.has(column))) {
      const weight = currentNodeColumns.has('is_heading') && currentNodeColumns.has('heading_level')
        ? "CASE WHEN is_heading=1 THEN CASE heading_level WHEN 1 THEN 50 WHEN 2 THEN 25 WHEN 3 THEN 12 WHEN 4 THEN 6 WHEN 5 THEN 3 ELSE 1 END ELSE 1 END"
        : '1';
      this.db.run('PRAGMA foreign_keys=OFF');
      this.db.run(`CREATE TABLE work_nodes_next (
        id INTEGER PRIMARY KEY AUTOINCREMENT, entry_id INTEGER REFERENCES input_entries(id) ON DELETE CASCADE,
        source_id INTEGER REFERENCES sources(id) ON DELETE CASCADE, user_id INTEGER REFERENCES users(id) ON DELETE SET NULL,
        kind TEXT NOT NULL CHECK(kind IN ('note','event','question')), text TEXT NOT NULL,
        ordinal INTEGER NOT NULL DEFAULT 0, weight INTEGER NOT NULL DEFAULT 1 CHECK(weight >= 1),
        state TEXT NOT NULL, scheduled_at TEXT, created_at TEXT NOT NULL, remote_id INTEGER,
        CHECK ((entry_id IS NOT NULL) != (source_id IS NOT NULL))
      )`);
      this.db.run(`INSERT INTO work_nodes_next(id,entry_id,source_id,user_id,kind,text,ordinal,weight,state,scheduled_at,created_at,remote_id)
        SELECT id,entry_id,source_id,user_id,kind,text,ordinal,${weight},state,scheduled_at,created_at,remote_id FROM work_nodes`);
      this.db.run('DROP TABLE work_nodes');
      this.db.run('ALTER TABLE work_nodes_next RENAME TO work_nodes');
      this.db.run('PRAGMA foreign_keys=ON');
    }
    add('summaries', 'level', 'level INTEGER NOT NULL DEFAULT 1');
    add('summaries', 'ordinal', 'ordinal INTEGER NOT NULL DEFAULT 0');
    const summaryColumns = new Set((this.db.exec('PRAGMA table_info(summaries)')[0]?.values || []).map((row) => String(row[1])));
    const summarySql = String(records(this.db, "SELECT sql FROM sqlite_master WHERE type='table' AND name='summaries'")[0]?.sql || '');
    if (!summaryColumns.has('start_ordinal') || !summaryColumns.has('end_ordinal') || summaryColumns.has('heading_path') || summarySql.includes('source_id INTEGER NOT NULL UNIQUE')) {
      this.db.run('PRAGMA foreign_keys=OFF');
      this.db.run(`CREATE TABLE summaries_next (
        id INTEGER PRIMARY KEY AUTOINCREMENT, source_id INTEGER NOT NULL REFERENCES sources(id) ON DELETE CASCADE,
        user_id INTEGER REFERENCES users(id) ON DELETE SET NULL, text TEXT NOT NULL,
        level INTEGER NOT NULL DEFAULT 1 CHECK(level >= 1), ordinal INTEGER NOT NULL DEFAULT 0,
        start_ordinal INTEGER NOT NULL DEFAULT 0, end_ordinal INTEGER NOT NULL DEFAULT 0,
        created_at TEXT NOT NULL, remote_id INTEGER, UNIQUE(source_id,level,ordinal)
      )`);
      const startOrdinal = summaryColumns.has('start_ordinal') ? 'start_ordinal' : 'ordinal';
      const endOrdinal = summaryColumns.has('end_ordinal') ? 'end_ordinal' : 'ordinal';
      this.db.run(`INSERT INTO summaries_next(id,source_id,user_id,text,level,ordinal,start_ordinal,end_ordinal,created_at,remote_id)
        SELECT id,source_id,user_id,text,level,ordinal,${startOrdinal},${endOrdinal},created_at,remote_id FROM summaries`);
      this.db.run('DROP TABLE summaries');
      this.db.run('ALTER TABLE summaries_next RENAME TO summaries');
      this.db.run('PRAGMA foreign_keys=ON');
    }
    const extractionColumns = new Set((this.db.exec('PRAGMA table_info(extractions)')[0]?.values || []).map((row) => String(row[1])));
    if (!extractionColumns.has('start_char') || !extractionColumns.has('end_char') || extractionColumns.has('text')) {
      this.db.run('PRAGMA foreign_keys=OFF');
      this.db.run(`CREATE TABLE extractions_next (
        id INTEGER PRIMARY KEY AUTOINCREMENT, node_id INTEGER NOT NULL REFERENCES work_nodes(id) ON DELETE CASCADE,
        dimension_key TEXT NOT NULL, start_char INTEGER NOT NULL CHECK(start_char >= 0),
        end_char INTEGER NOT NULL CHECK(end_char >= start_char), created_at TEXT NOT NULL, remote_id INTEGER
      )`);
      this.db.run(`INSERT INTO extractions_next(id,node_id,dimension_key,start_char,end_char,created_at,remote_id)
        SELECT x.id,x.node_id,x.dimension_key,
          CASE WHEN instr(n.text,x.text)>0 THEN instr(n.text,x.text)-1 ELSE 0 END,
          CASE WHEN instr(n.text,x.text)>0 THEN instr(n.text,x.text)+length(x.text)-2 ELSE max(length(n.text)-1,0) END,
          x.created_at,x.remote_id FROM extractions x JOIN work_nodes n ON n.id=x.node_id`);
      this.db.run('DROP TABLE extractions');
      this.db.run('ALTER TABLE extractions_next RENAME TO extractions');
      this.db.run('PRAGMA foreign_keys=ON');
    }
    const enrichmentSql = String(records(this.db, "SELECT sql FROM sqlite_master WHERE type='table' AND name='enrichment_jobs'")[0]?.sql || '');
    if (!enrichmentSql.includes("'failed'")) {
      this.db.run(`CREATE TABLE enrichment_jobs_next (
        id INTEGER PRIMARY KEY AUTOINCREMENT, job_key TEXT NOT NULL UNIQUE, payload TEXT NOT NULL,
        status TEXT NOT NULL CHECK(status IN ('pending','running','completed','failed')), attempts INTEGER NOT NULL DEFAULT 0,
        next_attempt_at TEXT NOT NULL, last_error TEXT, created_at TEXT NOT NULL, updated_at TEXT NOT NULL
      )`);
      this.db.run('INSERT INTO enrichment_jobs_next SELECT * FROM enrichment_jobs');
      this.db.run('DROP TABLE enrichment_jobs');
      this.db.run('ALTER TABLE enrichment_jobs_next RENAME TO enrichment_jobs');
    }
    for (const row of records(this.db, 'SELECT id,payload FROM enrichment_jobs')) {
      try {
        const payload = JSON.parse(String(row.payload)) as Record<string, unknown>;
        const document = payload.document as Record<string, unknown> | undefined;
        if (!document || !Array.isArray(document.notes)) continue;
        document.notes = document.notes.map((value) => {
          const note = value as Record<string, unknown>; const level = Number(note.headingLevel || 6);
          const legacyWeight = note.isHeading ? Math.max(1, Math.floor(50 / (2 ** (level - 1)))) : 1;
          return { text: String(note.text), ordinal: Number(note.ordinal || 0), weight: Math.max(1, Number(note.weight || legacyWeight)) };
        });
        this.db.run('UPDATE enrichment_jobs SET payload=? WHERE id=?', [JSON.stringify(payload), Number(row.id)]);
      } catch { /* A malformed job remains inspectable and will fail explicitly if claimed. */ }
    }
    const extractionFormatFailures = [
      'Ответ извлечения не является маркированным списком точных цитат',
      'Извлечение должно быть одной точной непрерывной цитатой исходного узла',
    ];
    const affectedJobs = records(this.db, `SELECT id FROM enrichment_jobs WHERE status!='completed' AND (${extractionFormatFailures.map(() => 'last_error=?').join(' OR ')})`, extractionFormatFailures);
    for (const row of affectedJobs) {
      this.db.run("UPDATE enrichment_jobs SET status='pending',attempts=0,next_attempt_at=?,last_error=NULL,updated_at=? WHERE id=?", [new Date().toISOString(), new Date().toISOString(), Number(row.id)]);
      this.db.run("DELETE FROM enrichment_task_results WHERE job_id=? AND task_key LIKE 'llm:extract:%'", [Number(row.id)]);
    }
    const resumedAt = new Date().toISOString();
    const immediatelyDueAt = new Date(Date.now() - 1_000).toISOString();
    this.db.run("UPDATE enrichment_jobs SET status='pending',next_attempt_at=?,updated_at=? WHERE status IN ('running','failed')", [immediatelyDueAt, resumedAt]);
    const memberSql = String(records(this.db, "SELECT sql FROM sqlite_master WHERE type='table' AND name='cluster_members'")[0]?.sql || '');
    if (!memberSql.includes("'source'")) {
      this.db.run('PRAGMA foreign_keys=OFF');
      this.db.run(`CREATE TABLE cluster_members_next (
        cluster_id INTEGER NOT NULL REFERENCES clusters(id) ON DELETE CASCADE,
        member_kind TEXT NOT NULL CHECK(member_kind IN ('source','entry','node','summary','extraction','cluster')),
        member_id INTEGER NOT NULL, PRIMARY KEY(cluster_id,member_kind,member_id)
      )`);
      this.db.run('INSERT INTO cluster_members_next SELECT cluster_id,member_kind,member_id FROM cluster_members');
      this.db.run('DROP TABLE cluster_members');
      this.db.run('ALTER TABLE cluster_members_next RENAME TO cluster_members');
      this.db.run('PRAGMA foreign_keys=ON');
    }
    this.db.run('CREATE INDEX IF NOT EXISTS work_nodes_entry_order_idx ON work_nodes(entry_id,ordinal)');
    this.db.run('CREATE INDEX IF NOT EXISTS work_nodes_source_order_idx ON work_nodes(source_id,ordinal)');
    this.db.run('CREATE INDEX IF NOT EXISTS clusters_user_dimension_level_idx ON clusters(user_id,dimension_key,level)');
    this.db.run('CREATE INDEX IF NOT EXISTS cluster_members_member_idx ON cluster_members(member_kind,member_id)');
    this.db.run('CREATE INDEX IF NOT EXISTS work_nodes_parent_ordinal_idx ON work_nodes(entry_id,source_id,ordinal)');
    this.db.run('CREATE INDEX IF NOT EXISTS extractions_node_idx ON extractions(node_id)');
    this.db.run('CREATE INDEX IF NOT EXISTS extractions_dimension_idx ON extractions(dimension_key)');
    this.db.run('CREATE UNIQUE INDEX IF NOT EXISTS extractions_identity_idx ON extractions(node_id,dimension_key,start_char,end_char)');
    this.db.run('CREATE INDEX IF NOT EXISTS enrichment_jobs_due_idx ON enrichment_jobs(status,next_attempt_at)');
    this.db.run('CREATE UNIQUE INDEX IF NOT EXISTS summaries_source_level_order_idx ON summaries(source_id,level,ordinal)');
    const retired = RETIRED_DIMENSION_KEYS.map(() => '?').join(',');
    this.db.run(`DELETE FROM embeddings WHERE dimension_key IN (${retired})
      OR (owner_kind='extraction' AND owner_id IN (SELECT id FROM extractions WHERE dimension_key IN (${retired})))
      OR (owner_kind='cluster' AND owner_id IN (SELECT id FROM clusters WHERE dimension_key IN (${retired})))`, [...RETIRED_DIMENSION_KEYS, ...RETIRED_DIMENSION_KEYS, ...RETIRED_DIMENSION_KEYS]);
    this.db.run(`DELETE FROM clusters WHERE dimension_key IN (${retired})`, RETIRED_DIMENSION_KEYS);
    this.db.run(`DELETE FROM extractions WHERE dimension_key IN (${retired})`, RETIRED_DIMENSION_KEYS);
    this.db.run('DELETE FROM explanation_edges WHERE from_extraction_id NOT IN (SELECT id FROM extractions) OR to_extraction_id NOT IN (SELECT id FROM extractions)');
    this.db.run(`DELETE FROM embeddings WHERE
      (owner_kind='source' AND NOT EXISTS (SELECT 1 FROM sources s WHERE s.id=embeddings.owner_id)) OR
      (owner_kind='entry' AND NOT EXISTS (SELECT 1 FROM input_entries i WHERE i.id=embeddings.owner_id)) OR
      (owner_kind='node' AND NOT EXISTS (SELECT 1 FROM work_nodes n WHERE n.id=embeddings.owner_id)) OR
      (owner_kind='summary' AND NOT EXISTS (SELECT 1 FROM summaries s WHERE s.id=embeddings.owner_id)) OR
      (owner_kind='extraction' AND NOT EXISTS (SELECT 1 FROM extractions x WHERE x.id=embeddings.owner_id)) OR
      (owner_kind='cluster' AND NOT EXISTS (SELECT 1 FROM clusters c WHERE c.id=embeddings.owner_id)) OR
      owner_kind NOT IN ('source','entry','node','summary','extraction','cluster')`);
  }

  private dirty() {
    this.db?.run("INSERT INTO sync_state(key,value) VALUES ('dirty','1') ON CONFLICT(key) DO UPDATE SET value=CAST(value AS INTEGER)+1");
  }

  private async persist() {
    if (!this.db) return;
    if (this.writeBatchDepth > 0) {
      this.writeBatchDirty = true;
      return;
    }
    const snapshot = this.db.export();
    this.persistQueue = this.persistQueue.catch(() => undefined).then(() => writeDatabase(this.file, snapshot));
    await this.persistQueue;
  }

  async runWriteBatch<T>(task: () => Promise<T>): Promise<T> {
    this.writeBatchDepth++;
    try {
      return await task();
    } finally {
      this.writeBatchDepth--;
      if (this.writeBatchDepth === 0 && this.writeBatchDirty) {
        this.writeBatchDirty = false;
        await this.persist();
      }
    }
  }

  async listTimeline(_userKey: string): Promise<TimelineItem[]> {
    const db = await this.database();
    const result = db.exec(`SELECT i.id,i.text,i.state,i.created_at,i.scheduled_at,i.source_kind,s.id,s.name,s.mime_type
      FROM input_entries i LEFT JOIN sources s ON s.id=i.source_id
      ORDER BY i.created_at DESC LIMIT 200`)[0];
    if (!result) return [];
    return result.values.map((row) => ({
      id: Number(row[0]), text: String(row[1]), state: String(row[2]) as EntryState,
      createdAt: String(row[3]), scheduledAt: row[4] ? String(row[4]) : null,
      source: String(row[5]) as TimelineItem['source'],
      attachment: row[6] == null ? null : { id: Number(row[6]), name: String(row[7]), mimeType: row[8] ? String(row[8]) : null },
    }));
  }

  async createEntry(input: CreateEntry): Promise<TimelineItem> {
    const db = await this.database();
    const now = new Date().toISOString();
    db.run(`INSERT INTO input_entries(source_id,text,source_kind,state,created_at,updated_at,user_id) VALUES (?, ?, ?, ?, ?, ?, ?)`, [input.sourceId ?? null, input.text, input.source, input.state, now, now, input.userId ?? 1]);
    const id = Number(db.exec('SELECT last_insert_rowid()')[0]?.values[0]?.[0]);
    const payload = { id, text: input.text, state: input.state, source: input.source, source_id: input.sourceId ?? null, user_id: input.userId ?? 1, created_at: now };
    db.run(`INSERT INTO outbox(operation, entity_id, payload, created_at) VALUES ('entry.upsert', ?, ?, ?)`, [id, JSON.stringify(payload), now]);
    this.dirty();
    await this.persist();
    return { id, text: input.text, state: input.state, createdAt: now, scheduledAt: null, source: input.source };
  }

  async updateState(id: number, state: EntryState): Promise<void> {
    const db = await this.database();
    const now = new Date().toISOString();
    db.run('UPDATE input_entries SET state = ?, updated_at = ? WHERE id = ?', [state, now, id]);
    db.run(`INSERT INTO outbox(operation, entity_id, payload, created_at) VALUES ('entry.state', ?, ?, ?)`, [id, JSON.stringify({ id, state }), now]);
    this.dirty();
    await this.persist();
  }

  async ensureUser(externalKey: string, name: string): Promise<number> {
    const db = await this.database();
    db.run(`INSERT INTO users(external_key,name,created_at) VALUES (?,?,?)
      ON CONFLICT(external_key) DO UPDATE SET name=excluded.name`, [externalKey, name, new Date().toISOString()]);
    const safe = externalKey.replaceAll("'", "''");
    const id = Number(db.exec(`SELECT id FROM users WHERE external_key='${safe}'`)[0]?.values[0]?.[0]);
    await this.persist(); return id;
  }

  async createSource(input: Omit<SourceRecord, 'id'> & { content?: Uint8Array }): Promise<SourceRecord> {
    const db = await this.database();
    const createdAt = new Date().toISOString();
    db.run('INSERT INTO sources(kind,name,mime_type,content,created_at,user_id) VALUES (?,?,?,?,?,?)', [input.kind, input.name, input.mimeType ?? null, input.content ?? null, createdAt, input.userId ?? 1]);
    const id = Number(db.exec('SELECT last_insert_rowid()')[0]?.values[0]?.[0]);
    this.dirty();
    await this.persist();
    return { id, kind: input.kind, name: input.name, mimeType: input.mimeType, uri: input.uri, userId: input.userId ?? 1 };
  }

  async createNode(input: Omit<WorkNodeRecord, 'id'>): Promise<WorkNodeRecord> {
    const db = await this.database();
    const node = this.upsertNode(db, input);
    this.dirty();
    await this.persist();
    return node;
  }

  private upsertNode(db: Database, input: Omit<WorkNodeRecord, 'id'>): WorkNodeRecord {
    const createdAt = new Date().toISOString();
    const existing = records(db, 'SELECT id FROM work_nodes WHERE entry_id IS ? AND source_id IS ? AND ordinal=? ORDER BY id LIMIT 1', [input.entryId ?? null, input.sourceId ?? null, input.ordinal ?? 0])[0];
    let id: number;
    if (existing) {
      id = Number(existing.id);
      db.run('UPDATE work_nodes SET user_id=?,kind=?,text=?,weight=? WHERE id=?', [input.userId ?? 1, input.kind, input.text, input.weight, id]);
    } else {
      db.run(`INSERT INTO work_nodes(entry_id,source_id,user_id,kind,text,ordinal,weight,state,created_at) VALUES (?,?,?,?,?,?,?,'active',?)`,
        [input.entryId ?? null, input.sourceId ?? null, input.userId ?? 1, input.kind, input.text, input.ordinal ?? 0, input.weight, createdAt]);
      id = Number(db.exec('SELECT last_insert_rowid()')[0]?.values[0]?.[0]);
    }
    return { id, ...input };
  }

  async createNodes(inputs: Array<Omit<WorkNodeRecord, 'id'>>): Promise<WorkNodeRecord[]> {
    if (!inputs.length) return [];
    const db = await this.database();
    db.run('BEGIN');
    try {
      const nodes = inputs.map((input) => this.upsertNode(db, input));
      db.run('COMMIT');
      this.dirty();
      await this.persist();
      return nodes;
    } catch (error) {
      db.run('ROLLBACK');
      throw error;
    }
  }

  async createSummary(input: Omit<SummaryRecord, 'id'>): Promise<SummaryRecord> {
    const db = await this.database();
    const source = records(db, 'SELECT kind FROM sources WHERE id=?', [input.sourceId])[0];
    if (!source) throw new Error('Документ для summary не найден');
    if (source.kind !== 'document') throw new Error('Summary можно создать только для документа');
    const createdAt = new Date().toISOString();
    db.run(`INSERT INTO summaries(source_id,user_id,text,level,ordinal,start_ordinal,end_ordinal,created_at) VALUES (?,?,?,?,?,?,?,?)
      ON CONFLICT(source_id,level,ordinal) DO UPDATE SET user_id=excluded.user_id,text=excluded.text,start_ordinal=excluded.start_ordinal,end_ordinal=excluded.end_ordinal,created_at=excluded.created_at`,
      [input.sourceId, input.userId ?? 1, input.text, input.level, input.ordinal, input.startOrdinal, input.endOrdinal, createdAt]);
    const row = records(db, 'SELECT id FROM summaries WHERE source_id=? AND level=? AND ordinal=?', [input.sourceId, input.level, input.ordinal])[0];
    const id = Number(row.id);
    this.dirty(); await this.persist();
    return { id, ...input };
  }

  async createExtraction(input: Omit<ExtractionRecord, 'id'>): Promise<ExtractionRecord> {
    const db = await this.database();
    const createdAt = new Date().toISOString();
    const node = records(db, 'SELECT length(text) AS length FROM work_nodes WHERE id=?', [input.nodeId])[0];
    if (!node || input.startChar < 0 || input.endChar < input.startChar || input.endChar >= Number(node.length)) throw new Error('Диапазон извлечения выходит за границы узла');
    const existing = records(db, 'SELECT id FROM extractions WHERE node_id=? AND dimension_key=? AND start_char=? AND end_char=? ORDER BY id LIMIT 1', [input.nodeId, input.dimensionKey, input.startChar, input.endChar])[0];
    const id = existing ? Number(existing.id) : (() => {
      db.run('INSERT INTO extractions(node_id,dimension_key,start_char,end_char,created_at) VALUES (?,?,?,?,?)', [input.nodeId, input.dimensionKey, input.startChar, input.endChar, createdAt]);
      return Number(db.exec('SELECT last_insert_rowid()')[0]?.values[0]?.[0]);
    })();
    this.dirty();
    await this.persist();
    return { id, ...input };
  }

  async createEdge(fromExtractionId: number, toExtractionId: number, relation: string): Promise<void> {
    const db = await this.database();
    const allowed = ['confirms', 'cause', 'goal_basis', 'value_basis', 'normative_basis', 'constraint_basis', 'context', 'alias', 'causes', 'prevents', 'enables', 'motivates', 'results_in', 'evidence_for', 'corefers_to', 'temporally_before', 'contrasts_with'];
    const scoped = fromExtractionId !== toExtractionId && records(db, `SELECT 1 AS valid FROM extractions xf JOIN work_nodes nf ON nf.id=xf.node_id
      JOIN extractions xt ON xt.id=? JOIN work_nodes nt ON nt.id=xt.node_id
      WHERE xf.id=? AND (nf.id=nt.id OR (?!='alias' AND (
        (nf.source_id IS NOT NULL AND nf.source_id=nt.source_id) OR
        (nf.entry_id IS NOT NULL AND nf.entry_id=nt.entry_id)
      )))`, [toExtractionId, fromExtractionId, relation]).length > 0;
    if (!allowed.includes(relation) || !scoped) throw new Error('Недопустимая объясняющая связь');
    db.run('INSERT OR IGNORE INTO explanation_edges(from_extraction_id,to_extraction_id,relation) VALUES (?,?,?)', [fromExtractionId, toExtractionId, relation]);
    this.dirty();
    await this.persist();
  }

  async saveTeacherAnnotation(input: TeacherAnnotationInput): Promise<void> {
    const db = await this.database();
    const createdAt = new Date().toISOString();
    db.run(`INSERT INTO teacher_annotations(entry_id,user_id,source_text,model,prompt_key,prompt_version,schema_version,tokenizer_version,input_hash,payload,created_at)
      VALUES (?,?,?,?,?,?,?,?,?,?,?)
      ON CONFLICT(entry_id) DO UPDATE SET user_id=excluded.user_id,source_text=excluded.source_text,model=excluded.model,
      prompt_key=excluded.prompt_key,prompt_version=excluded.prompt_version,schema_version=excluded.schema_version,
      tokenizer_version=excluded.tokenizer_version,input_hash=excluded.input_hash,payload=excluded.payload,created_at=excluded.created_at`,
      [input.entryId, input.userId, input.sourceText, input.model, input.promptKey, input.promptVersion, input.schemaVersion, input.tokenizerVersion, input.inputHash, JSON.stringify(input.payload), createdAt]);
    this.dirty();
    await this.persist();
  }

  async putEmbedding(ownerKind: EmbeddingOwnerKind, ownerId: number, dimensionKey: string | null, vector: number[]): Promise<void> {
    const db = await this.database();
    const createdAt = new Date().toISOString();
    db.run(`INSERT INTO embeddings(owner_kind,owner_id,dimension_key,model,dimensions,vector,created_at) VALUES (?,?,?,?,?,?,?)
      ON CONFLICT(owner_kind,owner_id,dimension_key,model) DO UPDATE SET dimensions=excluded.dimensions,vector=excluded.vector,created_at=excluded.created_at`,
      [ownerKind, ownerId, dimensionKey, 'text-embedding-3-large', vector.length, JSON.stringify(vector), createdAt]);
    this.dirty();
    await this.persist();
  }

  async listClusterInputs(userId?: number): Promise<ClusterInput[]> {
    const db = await this.database();
    const result = db.exec(`SELECT owner_kind,id,dimension_key,text,vector,user_id,weight FROM (
      SELECT 'source' AS owner_kind,s.id,? AS dimension_key,
        (SELECT group_concat(summary_text, char(10)||char(10)) FROM (SELECT ss.text AS summary_text FROM summaries ss WHERE ss.source_id=s.id AND ss.level=(SELECT max(level) FROM summaries sm WHERE sm.source_id=s.id) ORDER BY ordinal)) AS text,
        e.vector,s.user_id,1 AS weight
        FROM sources s JOIN embeddings e ON e.owner_kind='source' AND e.owner_id=s.id AND e.dimension_key=?
      UNION ALL
      SELECT 'entry' AS owner_kind,i.id,? AS dimension_key,i.text,e.vector,i.user_id,1 AS weight
        FROM input_entries i JOIN embeddings e ON e.owner_kind='entry' AND e.owner_id=i.id AND e.dimension_key=?
      UNION ALL
      SELECT 'node',n.id,?,n.text,e.vector,n.user_id,n.weight
        FROM work_nodes n JOIN embeddings e ON e.owner_kind='node' AND e.owner_id=n.id AND e.dimension_key=?
        WHERE n.kind IN ('note','event','question')
      UNION ALL
      SELECT 'summary',s.id,?,s.text,e.vector,s.user_id,1
        FROM summaries s JOIN embeddings e ON e.owner_kind='summary' AND e.owner_id=s.id AND e.dimension_key=?
      UNION ALL
      SELECT 'extraction',x.id,x.dimension_key,substr(n.text,x.start_char+1,x.end_char-x.start_char+1),e.vector,n.user_id,n.weight
        FROM extractions x JOIN embeddings e ON e.owner_kind='extraction' AND e.owner_id=x.id AND e.dimension_key=x.dimension_key
        JOIN work_nodes n ON n.id=x.node_id
    ) WHERE (? IS NULL OR user_id=?) ORDER BY dimension_key,owner_kind,id`, [CONTENT_DIMENSION_KEY, CONTENT_DIMENSION_KEY, CONTENT_DIMENSION_KEY, CONTENT_DIMENSION_KEY, CONTENT_DIMENSION_KEY, CONTENT_DIMENSION_KEY, CONTENT_DIMENSION_KEY, CONTENT_DIMENSION_KEY, userId ?? null, userId ?? null])[0];
    return result?.values.map((row) => ({ ownerKind: String(row[0]) as ClusterLeafKind, id: Number(row[1]), dimensionKey: String(row[2]), text: String(row[3]), vector: JSON.parse(String(row[4])) as number[], userId: row[5] == null ? undefined : Number(row[5]), weight: Math.max(1, Number(row[6] || 1)) })) ?? [];
  }

  async listClusterVectorCache(userId?: number): Promise<ClusterVectorCache[]> {
    const db = await this.database();
    const result = db.exec(`SELECT c.dimension_key,c.level,c.text,e.vector FROM clusters c
      JOIN embeddings e ON e.owner_kind='cluster' AND e.owner_id=c.id AND e.dimension_key=c.dimension_key
      WHERE (? IS NULL OR c.user_id=?) ORDER BY c.dimension_key,c.level,c.id`, [userId ?? null, userId ?? null])[0];
    return result?.values.map((row) => ({ dimensionKey: String(row[0]), level: Number(row[1]), text: String(row[2]), vector: JSON.parse(String(row[3])) as number[] })) ?? [];
  }

  async rebuildClusters(clusters: ClusterDraft[], userId?: number): Promise<void> {
    const db = await this.database();
    db.run('BEGIN');
    try {
      db.run("DELETE FROM embeddings WHERE owner_kind='cluster' AND owner_id IN (SELECT id FROM clusters WHERE (? IS NULL OR user_id=?))", [userId ?? null, userId ?? null]);
      db.run("DELETE FROM cluster_members WHERE cluster_id IN (SELECT id FROM clusters WHERE (? IS NULL OR user_id=?))", [userId ?? null, userId ?? null]);
      db.run("DELETE FROM clusters WHERE (? IS NULL OR user_id=?)", [userId ?? null, userId ?? null]);
      const ids = new Map<string, number>();
      for (const cluster of [...clusters].sort((a, b) => a.level - b.level)) {
        const createdAt = new Date().toISOString();
        db.run('INSERT INTO clusters(dimension_key,level,text,embedding,created_at,user_id) VALUES (?,?,?,?,?,?)', [cluster.dimensionKey, cluster.level, cluster.text, JSON.stringify(cluster.vector), createdAt, cluster.userId ?? null]);
        const id = Number(db.exec('SELECT last_insert_rowid()')[0]?.values[0]?.[0]); ids.set(cluster.clientKey, id);
        for (const member of cluster.members) {
          const memberId = member.kind === 'cluster' ? ids.get(member.clientKey) : member.id;
          if (memberId) db.run('INSERT INTO cluster_members(cluster_id,member_kind,member_id) VALUES (?,?,?)', [id, member.kind, memberId]);
        }
        db.run('INSERT INTO embeddings(owner_kind,owner_id,dimension_key,model,dimensions,vector,created_at) VALUES (?,?,?,?,?,?,?)', ['cluster', id, cluster.dimensionKey, 'text-embedding-3-large', cluster.vector.length, JSON.stringify(cluster.vector), createdAt]);
      }
      db.run('COMMIT'); this.dirty(); await this.persist();
    } catch (error) { db.run('ROLLBACK'); throw error; }
  }

  async enqueueEnrichment(payload: EnrichmentPayload): Promise<void> {
    const db = await this.database();
    const now = new Date().toISOString();
    const jobKey = payload.document ? `source:${payload.document.sourceId}` : `entry:${payload.entryId}`;
    db.run(`INSERT INTO enrichment_jobs(job_key,payload,status,attempts,next_attempt_at,last_error,created_at,updated_at)
      VALUES (?,?,'pending',0,?,NULL,?,?)
      ON CONFLICT(job_key) DO UPDATE SET
        payload=CASE WHEN enrichment_jobs.status='completed' THEN enrichment_jobs.payload ELSE excluded.payload END,
        status=CASE WHEN enrichment_jobs.status='completed' THEN 'completed' ELSE 'pending' END,
        next_attempt_at=CASE WHEN enrichment_jobs.status='completed' THEN enrichment_jobs.next_attempt_at ELSE excluded.next_attempt_at END,
        last_error=CASE WHEN enrichment_jobs.status='completed' THEN enrichment_jobs.last_error ELSE NULL END,
        updated_at=excluded.updated_at`,
      [jobKey, JSON.stringify(payload), now, now, now]);
    await this.persist();
  }

  async listDueEnrichments(at: string): Promise<EnrichmentJob[]> {
    const db = await this.database();
    const rows = records(db, `SELECT id,payload,attempts,next_attempt_at,last_error FROM enrichment_jobs
      WHERE status IN ('pending','running') AND next_attempt_at<=? ORDER BY next_attempt_at,id LIMIT 10`, [at]);
    const jobs = rows.map((row) => ({ id: Number(row.id), payload: JSON.parse(String(row.payload)) as EnrichmentPayload, attempts: Number(row.attempts), nextAttemptAt: String(row.next_attempt_at), lastError: row.last_error == null ? null : String(row.last_error) }));
    const leaseUntil = new Date(Date.parse(at) + 5 * 60_000).toISOString();
    for (const job of jobs) db.run("UPDATE enrichment_jobs SET status='running',next_attempt_at=?,updated_at=? WHERE id=?", [leaseUntil, at, job.id]);
    if (jobs.length) await this.persist();
    return jobs;
  }

  async completeEnrichment(id: number): Promise<void> {
    const db = await this.database();
    db.run("UPDATE enrichment_jobs SET status='completed',last_error=NULL,updated_at=? WHERE id=?", [new Date().toISOString(), id]);
    db.run('DELETE FROM enrichment_task_results WHERE job_id=?', [id]);
    await this.persist();
  }

  async retryEnrichment(id: number, nextAttemptAt: string, error: string): Promise<void> {
    const db = await this.database();
    db.run("UPDATE enrichment_jobs SET status='pending',attempts=attempts+1,next_attempt_at=?,last_error=?,updated_at=? WHERE id=?", [nextAttemptAt, error.slice(0, 2_000), new Date().toISOString(), id]);
    await this.persist();
  }

  async failEnrichment(id: number, error: string): Promise<void> {
    const db = await this.database();
    db.run("UPDATE enrichment_jobs SET status='failed',last_error=?,updated_at=? WHERE id=?", [error.slice(0, 2_000), new Date().toISOString(), id]);
    await this.persist();
  }

  async getEnrichmentTask(jobId: number, taskKey: string): Promise<EnrichmentTaskResult> {
    const db = await this.database();
    const row = records(db, 'SELECT result FROM enrichment_task_results WHERE job_id=? AND task_key=?', [jobId, taskKey])[0];
    if (!row) return { completed: false };
    try { return { completed: true, result: JSON.parse(String(row.result)) }; }
    catch { return { completed: false }; }
  }

  async completeEnrichmentTask(jobId: number, taskKey: string, result: unknown = null): Promise<void> {
    const db = await this.database();
    db.run(`INSERT INTO enrichment_task_results(job_id,task_key,result,completed_at) VALUES (?,?,?,?)
      ON CONFLICT(job_id,task_key) DO UPDATE SET result=excluded.result,completed_at=excluded.completed_at`,
      [jobId, taskKey, JSON.stringify(result ?? null), new Date().toISOString()]);
    await this.persist();
  }

  async listInterruptedDocuments(): Promise<InterruptedDocument[]> {
    const db = await this.database();
    return records(db, `SELECT s.id,s.name,s.mime_type,s.content,s.user_id,i.id AS entry_id,i.text AS entry_text
      FROM sources s LEFT JOIN input_entries i ON i.source_id=s.id
      WHERE s.kind='document' AND s.content IS NOT NULL AND s.remote_id IS NULL
      AND NOT EXISTS (SELECT 1 FROM enrichment_jobs j WHERE j.job_key='source:' || s.id)
      AND NOT EXISTS (SELECT 1 FROM work_nodes n WHERE n.source_id=s.id)
      ORDER BY s.id`).flatMap((row) => row.content instanceof Uint8Array ? [{
        sourceId: Number(row.id), name: String(row.name), mimeType: row.mime_type == null ? null : String(row.mime_type),
        userId: row.user_id == null ? 1 : Number(row.user_id), content: row.content,
        entryId: row.entry_id == null ? null : Number(row.entry_id), entryText: row.entry_text == null ? '' : String(row.entry_text),
      }] : []);
  }

  async sync(endpoint: string, accountKey: string) {
    const db = await this.database();
    const result = db.exec('SELECT id, operation, entity_id, payload, created_at FROM outbox ORDER BY id LIMIT 250')[0];
    const operations = result?.values.map((row) => ({ outbox_id: Number(row[0]), operation: row[1], entity_id: Number(row[2]), payload: JSON.parse(String(row[3])), created_at: row[4] })) ?? [];
    const deviceId = localStorage.getItem('graphite.device-id') || crypto.randomUUID();
    localStorage.setItem('graphite.device-id', deviceId);
    const lastAccount = String(records(db, "SELECT value FROM sync_state WHERE key='last_account'")[0]?.value || '');
    const syncRevision = Number(records(db, "SELECT value FROM sync_state WHERE key='dirty'")[0]?.value || 0);
    const isDirty = syncRevision > 0;
    const shouldPush = isDirty || lastAccount !== accountKey || operations.length > 0;
    const snapshot = shouldPush ? {
      sources: records(db, 'SELECT id,kind,name,mime_type,created_at,remote_id,content FROM sources').map(({ content, ...row }) => ({ ...row, content_base64: row.remote_id == null && content instanceof Uint8Array ? toBase64(content) : null })),
      entries: records(db, 'SELECT id,source_id,text,source_kind,state,scheduled_at,created_at,updated_at,remote_id FROM input_entries'),
      nodes: records(db, 'SELECT id,entry_id,source_id,kind,text,ordinal,weight,state,scheduled_at,created_at,remote_id FROM work_nodes'),
      summaries: records(db, 'SELECT id,source_id,user_id,text,level,ordinal,start_ordinal,end_ordinal,created_at,remote_id FROM summaries'),
      extractions: records(db, 'SELECT id,node_id,dimension_key,start_char,end_char,created_at,remote_id FROM extractions'),
      explanation_edges: records(db, 'SELECT id,from_extraction_id,to_extraction_id,relation,remote_id FROM explanation_edges'),
      teacher_annotations: records(db, 'SELECT id,entry_id,user_id,source_text,model,prompt_key,prompt_version,schema_version,tokenizer_version,input_hash,payload,created_at,remote_id FROM teacher_annotations')
        .map((row) => ({ ...row, payload: JSON.parse(String(row.payload)) })),
      clusters: records(db, 'SELECT id,dimension_key,level,text,created_at,remote_id FROM clusters'),
      cluster_members: records(db, 'SELECT cluster_id,member_kind,member_id FROM cluster_members'),
      embeddings: records(db, 'SELECT owner_kind,owner_id,dimension_key,model,dimensions,vector,created_at FROM embeddings'),
    } : undefined;
    const knownSourceContentIds = records(db, 'SELECT remote_id FROM sources WHERE remote_id IS NOT NULL AND content IS NOT NULL').map((row) => Number(row.remote_id));
    const response = await fetch(`${endpoint.replace(/\/$/, '')}/v1/sync`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ device_id: deviceId, cursor: 0, operations, snapshot, known_source_content_ids: knownSourceContentIds }),
    });
    if (!response.ok) {
      let detail = '';
      try {
        const error = await response.json() as { detail?: string };
        detail = error.detail ? `: ${error.detail}` : '';
      } catch { /* The status still remains useful for a non-JSON gateway response. */ }
      throw new Error(`Синхронизация недоступна (${response.status})${detail}`);
    }
    const body = await response.json() as {
      accepted_outbox_ids: number[];
      mappings: Array<{ entity_kind: string; local_id: number; remote_id: number }>;
      snapshot: Record<string, Array<Record<string, unknown>>>;
      warnings?: string[];
    };
    for (const outboxId of body.accepted_outbox_ids) db.run('DELETE FROM outbox WHERE id = ?', [outboxId]);
    const mappingTables: Record<string, string> = { source: 'sources', entry: 'input_entries', node: 'work_nodes', summary: 'summaries', extraction: 'extractions', edge: 'explanation_edges', annotation: 'teacher_annotations', cluster: 'clusters' };
    for (const mapping of body.mappings) {
      const table = mappingTables[mapping.entity_kind];
      if (table) db.run(`UPDATE ${table} SET remote_id=? WHERE id=?`, [mapping.remote_id, mapping.local_id]);
    }
    const currentRevision = Number(records(db, "SELECT value FROM sync_state WHERE key='dirty'")[0]?.value || 0);
    if (currentRevision !== syncRevision) {
      await this.persist();
      return { pushed: body.accepted_outbox_ids.length, pulled: 0, warnings: body.warnings || [] };
    }
    const localId = (table: string, remoteId: unknown) => Number(records(db, `SELECT id FROM ${table} WHERE remote_id=? LIMIT 1`, [Number(remoteId)])[0]?.id || 0) || null;
    const localUserId = await this.ensureUser(accountKey, accountKey);

    for (const source of body.snapshot.sources || []) {
      const remoteId = Number(source.id); const existing = localId('sources', remoteId); const content = fromBase64(source.content_base64);
      if (existing) db.run('UPDATE sources SET kind=?,name=?,mime_type=?,content=COALESCE(?,content),created_at=?,user_id=? WHERE id=?', [String(source.kind), String(source.name), source.mime_type == null ? null : String(source.mime_type), content, String(source.created_at), localUserId, existing]);
      else db.run('INSERT INTO sources(kind,name,mime_type,content,created_at,user_id,remote_id) VALUES (?,?,?,?,?,?,?)', [String(source.kind), String(source.name), source.mime_type == null ? null : String(source.mime_type), content, String(source.created_at), localUserId, remoteId]);
    }
    for (const entry of body.snapshot.entries || []) {
      const remoteId = Number(entry.id); const existing = localId('input_entries', remoteId);
      const sourceId = entry.source_id == null ? null : localId('sources', entry.source_id);
      const values: Array<string | number | null> = [sourceId, String(entry.text), String(entry.source_kind), String(entry.state), entry.scheduled_at == null ? null : String(entry.scheduled_at), String(entry.created_at), String(entry.updated_at), localUserId];
      if (existing) db.run('UPDATE input_entries SET source_id=?,text=?,source_kind=?,state=?,scheduled_at=?,created_at=?,updated_at=?,user_id=? WHERE id=?', [...values, existing]);
      else db.run('INSERT INTO input_entries(source_id,text,source_kind,state,scheduled_at,created_at,updated_at,user_id,remote_id) VALUES (?,?,?,?,?,?,?,?,?)', [...values, remoteId]);
    }
    for (const node of body.snapshot.nodes || []) {
      const remoteId = Number(node.id); const existing = localId('work_nodes', remoteId);
      const entryId = node.entry_id == null ? null : localId('input_entries', node.entry_id);
      const sourceId = node.source_id == null ? null : localId('sources', node.source_id);
      const values: Array<string | number | null> = [entryId, sourceId, localUserId, String(node.kind), String(node.text), Number(node.ordinal || 0), Math.max(1, Number(node.weight || 1)), String(node.state), node.scheduled_at == null ? null : String(node.scheduled_at), String(node.created_at)];
      if (existing) db.run('UPDATE work_nodes SET entry_id=?,source_id=?,user_id=?,kind=?,text=?,ordinal=?,weight=?,state=?,scheduled_at=?,created_at=? WHERE id=?', [...values, existing]);
      else db.run('INSERT INTO work_nodes(entry_id,source_id,user_id,kind,text,ordinal,weight,state,scheduled_at,created_at,remote_id) VALUES (?,?,?,?,?,?,?,?,?,?,?)', [...values, remoteId]);
    }
    for (const summary of body.snapshot.summaries || []) {
      const sourceId = localId('sources', summary.source_id); if (!sourceId) continue;
      const remoteId = Number(summary.id); const existing = localId('summaries', remoteId);
      const values: Array<string | number | null> = [sourceId, localUserId, String(summary.text), Number(summary.level || 1), Number(summary.ordinal || 0), Number(summary.start_ordinal ?? summary.ordinal ?? 0), Number(summary.end_ordinal ?? summary.ordinal ?? 0), String(summary.created_at)];
      if (existing) db.run('UPDATE summaries SET source_id=?,user_id=?,text=?,level=?,ordinal=?,start_ordinal=?,end_ordinal=?,created_at=? WHERE id=?', [...values, existing]);
      else db.run('INSERT INTO summaries(source_id,user_id,text,level,ordinal,start_ordinal,end_ordinal,created_at,remote_id) VALUES (?,?,?,?,?,?,?,?,?) ON CONFLICT(source_id,level,ordinal) DO UPDATE SET user_id=excluded.user_id,text=excluded.text,start_ordinal=excluded.start_ordinal,end_ordinal=excluded.end_ordinal,created_at=excluded.created_at,remote_id=excluded.remote_id', [...values, remoteId]);
    }
    for (const extraction of body.snapshot.extractions || []) {
      const nodeId = localId('work_nodes', extraction.node_id); if (!nodeId) continue;
      const remoteId = Number(extraction.id); const existing = localId('extractions', remoteId);
      const nodeText = String(records(db, 'SELECT text FROM work_nodes WHERE id=?', [nodeId])[0]?.text || '');
      const legacyText = String(extraction.text || '');
      const startChar = extraction.start_char == null ? nodeText.indexOf(legacyText) : Number(extraction.start_char);
      const endChar = extraction.end_char == null ? startChar + legacyText.length - 1 : Number(extraction.end_char);
      if (startChar < 0 || endChar < startChar || endChar >= nodeText.length) continue;
      const values: Array<string | number> = [nodeId, String(extraction.dimension_key), startChar, endChar, String(extraction.created_at)];
      if (existing) db.run('UPDATE extractions SET node_id=?,dimension_key=?,start_char=?,end_char=?,created_at=? WHERE id=?', [...values, existing]);
      else db.run('INSERT INTO extractions(node_id,dimension_key,start_char,end_char,created_at,remote_id) VALUES (?,?,?,?,?,?)', [...values, remoteId]);
    }
    for (const edge of body.snapshot.explanation_edges || []) {
      const fromId = localId('extractions', edge.from_extraction_id); const toId = localId('extractions', edge.to_extraction_id); if (!fromId || !toId) continue;
      const remoteId = Number(edge.id); const existing = localId('explanation_edges', remoteId);
      if (existing) db.run('UPDATE explanation_edges SET from_extraction_id=?,to_extraction_id=?,relation=? WHERE id=?', [fromId, toId, String(edge.relation), existing]);
      else {
        db.run('INSERT OR IGNORE INTO explanation_edges(from_extraction_id,to_extraction_id,relation,remote_id) VALUES (?,?,?,?)', [fromId, toId, String(edge.relation), remoteId]);
        db.run('UPDATE explanation_edges SET remote_id=? WHERE from_extraction_id=? AND to_extraction_id=? AND relation=?', [remoteId, fromId, toId, String(edge.relation)]);
      }
    }
    for (const annotation of body.snapshot.teacher_annotations || []) {
      const entryId = localId('input_entries', annotation.entry_id); if (!entryId) continue;
      const remoteId = Number(annotation.id); const existing = localId('teacher_annotations', remoteId);
      const rawPayload = typeof annotation.payload === 'string' ? annotation.payload : JSON.stringify(annotation.payload);
      const values: Array<string | number> = [entryId, localUserId, String(annotation.source_text), String(annotation.model), String(annotation.prompt_key), String(annotation.prompt_version), String(annotation.schema_version), String(annotation.tokenizer_version), String(annotation.input_hash), rawPayload, String(annotation.created_at)];
      if (existing) db.run('UPDATE teacher_annotations SET entry_id=?,user_id=?,source_text=?,model=?,prompt_key=?,prompt_version=?,schema_version=?,tokenizer_version=?,input_hash=?,payload=?,created_at=? WHERE id=?', [...values, existing]);
      else db.run('INSERT INTO teacher_annotations(entry_id,user_id,source_text,model,prompt_key,prompt_version,schema_version,tokenizer_version,input_hash,payload,created_at,remote_id) VALUES (?,?,?,?,?,?,?,?,?,?,?,?) ON CONFLICT(entry_id) DO UPDATE SET user_id=excluded.user_id,source_text=excluded.source_text,model=excluded.model,prompt_key=excluded.prompt_key,prompt_version=excluded.prompt_version,schema_version=excluded.schema_version,tokenizer_version=excluded.tokenizer_version,input_hash=excluded.input_hash,payload=excluded.payload,created_at=excluded.created_at,remote_id=excluded.remote_id', [...values, remoteId]);
    }
    for (const cluster of body.snapshot.clusters || []) {
      const remoteId = Number(cluster.id); const existing = localId('clusters', remoteId);
      const values: Array<string | number> = [String(cluster.dimension_key), Number(cluster.level), String(cluster.text), String(cluster.created_at), localUserId];
      if (existing) db.run('UPDATE clusters SET dimension_key=?,level=?,text=?,created_at=?,user_id=? WHERE id=?', [...values, existing]);
      else db.run('INSERT INTO clusters(dimension_key,level,text,created_at,user_id,remote_id) VALUES (?,?,?,?,?,?)', [...values, remoteId]);
    }
    for (const member of body.snapshot.cluster_members || []) {
      const clusterId = localId('clusters', member.cluster_id); const memberTable = mappingTables[String(member.member_kind)];
      const memberId = memberTable ? localId(memberTable, member.member_id) : null;
      if (clusterId && memberId) db.run('INSERT OR IGNORE INTO cluster_members(cluster_id,member_kind,member_id) VALUES (?,?,?)', [clusterId, String(member.member_kind), memberId]);
    }
    for (const embedding of body.snapshot.embeddings || []) {
      const ownerTable = mappingTables[String(embedding.owner_kind)]; const ownerId = ownerTable ? localId(ownerTable, embedding.owner_id) : null; if (!ownerId) continue;
      const vector = typeof embedding.embedding === 'string' ? embedding.embedding : JSON.stringify(embedding.embedding);
      db.run('DELETE FROM embeddings WHERE owner_kind=? AND owner_id=? AND dimension_key IS ? AND model=?', [String(embedding.owner_kind), ownerId, embedding.dimension_key == null ? null : String(embedding.dimension_key), String(embedding.model)]);
      db.run('INSERT INTO embeddings(owner_kind,owner_id,dimension_key,model,dimensions,vector,created_at) VALUES (?,?,?,?,?,?,?)', [String(embedding.owner_kind), ownerId, embedding.dimension_key == null ? null : String(embedding.dimension_key), String(embedding.model), Number(embedding.dimensions), vector, String(embedding.created_at)]);
    }
    db.run("INSERT INTO sync_state(key,value) VALUES ('last_account',?) ON CONFLICT(key) DO UPDATE SET value=excluded.value", [accountKey]);
    db.run("INSERT INTO sync_state(key,value) VALUES ('dirty','0') ON CONFLICT(key) DO UPDATE SET value='0'");
    await this.persist();
    return { pushed: body.accepted_outbox_ids.length, pulled: body.snapshot.entries?.length || 0, warnings: body.warnings || [] };
  }

  async resetSync(accountKey: string) {
    const db = await this.database();
    const current = String(records(db, "SELECT value FROM sync_state WHERE key='last_account'")[0]?.value || '');
    if (current === accountKey) db.run("DELETE FROM sync_state WHERE key='last_account'");
    await this.persist();
  }

  async clearAll() {
    const db = await this.database();
    db.run('PRAGMA foreign_keys=OFF');
    for (const table of ['cluster_members', 'embeddings', 'explanation_edges', 'extractions', 'teacher_annotations', 'summaries', 'work_nodes', 'clusters', 'input_entries', 'sources', 'outbox', 'sync_state', 'enrichment_task_results', 'enrichment_jobs']) db.run(`DELETE FROM ${table}`);
    db.run("DELETE FROM users WHERE external_key<>'default'");
    db.run('PRAGMA foreign_keys=ON');
    await this.persist();
  }
}
